// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// C₂ · UMA LINHA POR SERVIÇO — a secção dos subcontratados deixa de ter bandas
// ============================================================================
//
// 📗 Cliente (§4.3 de `docs/superpowers/specs/2026-09-11-agilidade-do-planeador-design.md`):
//    *«Os subcontratados têm de dar para ver os dias igualmente como se fosse um autocarro
//    normal; a única diferença é que não se indica o motorista.»*
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ A REGRA QUE ISTO FECHA, e é ela que manda — não o sintoma
// ---------------------------------------------------------------------------
//
//   > **A geometria e a selecção da secção dos subcontratados indexam-se pela LINHA — um
//   > serviço —, e nunca pela EMPRESA.**
//
//   O sintoma era *«o segundo serviço do dia não se vê»*. A regra é outra, e é maior: enquanto
//   a unidade da secção for a **empresa**, todo o gesto que aponta para «um dia de um
//   subcontratado» aponta para um conjunto com até **9** elementos (medido) — e escolhe um
//   deles por omissão, em silêncio. Era literalmente isso que o `detalheSub` fazia
//   (`extentoDoServico(linha, dia, 0)`: o intervalo do PRIMEIRO, sobre um painel que listava
//   todos), e o que o «Ver no mapa», o realce e o cartão do cursor faziam por arrasto.
//
// ---------------------------------------------------------------------------
// ⚠️ O QUE ISTO SUBSTITUI — B28, as bandas, e porque é que o argumento dele MORRE
// ---------------------------------------------------------------------------
//
//   B28 (2026-08-31) deu a cada empresa tantas **bandas de 14 px** quantas o seu pior dia
//   exigisse, com **tecto 4** e uma calha a contar o que não coubesse. Resolvia «ver os
//   serviços»; **não** resolvia «comportar-se como um autocarro»:
//
//   | | bandas (B28) | linhas (C₂) |
//   |---|---|---|
//   | serviços acima do tecto | contados numa calha, **não desenhados** | todos desenhados |
//   | o que uma selecção aponta | a empresa + o dia → **até 9 serviços** | **um** serviço |
//   | a geometria | altura variável por empresa (30 · 37 · 52 · 67 · 80 px) | uniforme, `ALTURA_LINHA_SUB` |
//   | navegar com as setas | declarado por fazer — *«a secção tem geometria própria»* | a mesma aritmética da frota |
//
//   📌 **O contra-facto de B28 continua de pé e é ele que torna isto barato:** medido na folha
//   real, **9 de 16** empresas nunca passam de UM serviço no mesmo dia em todo o ano, e
//   **78,3 %** dos dias-empresa têm um só. Uma linha por serviço dá **uma** linha a essas
//   empresas — exactamente o que tinham antes de B28 e depois dele.
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ O ORÇAMENTO, medido antes de construir (ADR-001 / risco R1)
// ---------------------------------------------------------------------------
//
//   Sobre a folha real (`sonda-b28-subcontratos-no-mapa.mjs` §1-bis para a simultaneidade):
//
//   | | pré-B28 | B28 (bandas) | **C₂ (linhas)** |
//   |---|---|---|---|
//   | linhas na secção | 15 | 15 | **39** |
//   | altura da secção | 450 px | 637 px | **1 170 px** |
//   | nós de barra na pior janela de 40 dias | 290 | 324 | **444** |
//   | nós totais da secção (barras + cromo) | 320 | 354 | **522** |
//
//   ⚠️⚠️ **O TECTO DE 1 400 CÉLULAS POR JANELA NÃO É TOCADO, e a razão verifica-se no código e
//   não por opinião:** `calcularJanela` recebe `{ viaturas: dados.viaturas.length, dias }` —
//   a secção dos subcontratados **não entra nesse produto**, porque não é virtualizada. O que
//   ela custa mede-se em **nós** e em **altura**, e é o que a tabela acima diz: 522 nós contra
//   os **3 246** que o ADR-001 mediu no mapa real (16 % dele).
//
//   ⚠️⚠️ **A TABELA ACIMA É A DO C₂ (2026-09-15) E FICA COMO REGISTO — mas a altura mudou no
//   dia seguinte.** A 2026-09-16 a linha ganhou a **banda das cidades por dia** (📗 *«com as
//   cidades por dias seguindo a mesma estrutura»*) e passou de 30 para **36 px**: com as 39
//   linhas medidas, 1 170 → **1 404 px**, mais **234**. E os nós sobem **+643** na pior janela
//   — o número, as três alternativas rejeitadas e o porquê estão em `local-do-dia.ts`.
//   📌 *Um número de estado desactualizado é pior do que nenhum*, e por isso está aqui e não
//   só lá.
//
//   📌 **O custo verdadeiro é a ALTURA: +533 px no C₂, e mais 234 agora.** Não se compra com um tecto —
//   um tecto é o que B28 tinha, e é exactamente a parte que falhava em 48 % dos dias (M7).
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ A ATRIBUIÇÃO É DO ANO E NÃO DA JANELA — e agora é obrigatório, não preferível
// ---------------------------------------------------------------------------
//
//   B28 calculava as bandas dentro da janela desenhada. Com **linhas**, isso seria um defeito
//   e não uma imprecisão: um serviço que mudasse de linha ao deslocar o mapa mudava de sítio
//   debaixo do olho, e a linha é agora o que a selecção, o realce e os botões apontam —
//   deslocar o mapa passaria a mudar o alvo de uma escrita. 📌 Por isso a atribuição corre
//   sobre o **ano inteiro**, uma vez, e a janela só decide o que se desenha.
// ============================================================================

import type { CelulaCompacta } from '@xlv-gva-mpt/dominio';
import { ALTURA_LINHA_SUB, acumular } from './dimensoes.js';
import { identidadeDoServico, ordenarServicosDoDia } from './barras-do-subcontratado.js';

/** Um dia sem serviço. Partilhado para não alocar 365 arrays vazios por linha. */
const SEM_SERVICO: readonly CelulaCompacta[] = Object.freeze([]);

/**
 * Uma **linha** da secção: um dos serviços simultâneos de uma empresa, ao longo do ano.
 *
 * ⚠️⚠️ **`dias` tem a MESMA FORMA da grelha de uma empresa** (`readonly CelulaCompacta[]` por
 * dia) e não `CelulaCompacta | null`. Não é descuido: é o que faz `extentoDoServico`,
 * `detalheSub` e `viagensDoSubcontratado` continuarem a funcionar **sem uma segunda versão** —
 * a linha é uma grelha de empresa que por construção nunca tem mais do que um serviço por dia.
 * 📌 Duas formulações da mesma leitura divergem, e a que divergisse seria a que ninguém corre.
 */
export interface LinhaDeSubcontratado {
  /** Índice da empresa em `dados.subcontratados` — é o que as rotas de escrita recebem. */
  readonly sub: number;
  /** 0-based dentro da empresa. `0` é a linha que leva o nome. */
  readonly ordem: number;
  /** Quantas linhas esta empresa tem. */
  readonly total: number;
  /** O ano inteiro: `dias[d]` tem **zero ou um** serviço. */
  readonly dias: readonly (readonly CelulaCompacta[])[];
}

/**
 * Reparte os serviços de cada empresa por linhas, **sobre o ano inteiro**.
 *
 * Uma linha nova só nasce quando um serviço começa e todas as que existem estão ocupadas —
 * logo cada empresa fica com tantas linhas quantos os serviços do seu **pior dia do ano**, que
 * é o mínimo possível. ⚠️ **Não há tecto:** um tecto é o que B28 tinha, e um serviço que não
 * cabe é um serviço que não se vê.
 *
 * ⚠️ **Uma empresa sem serviço nenhum fica com UMA linha.** Zero linhas fazia a empresa
 * desaparecer da secção — e a secção existe para dizer que ela existe.
 */
export function linhasDosSubcontratados(
  grelha: readonly (readonly (readonly CelulaCompacta[])[])[],
): readonly LinhaDeSubcontratado[] {
  const saida: LinhaDeSubcontratado[] = [];

  grelha.forEach((linhaDaEmpresa, sub) => {
    const linhas = [...repartirPorLinhas(linhaDaEmpresa)];

    // ⚠️ **Uma empresa sem serviço nenhum fica com UMA linha** — ver o contrato acima. É a
    //    única regra que NÃO vive em `repartirPorLinhas`, e de propósito: ela é da SECÇÃO dos
    //    parceiros («a secção existe para dizer que a empresa existe»), e a da espera não a tem
    //    — sem viagens em espera não há secção nenhuma para desenhar.
    if (linhas.length === 0) {
      linhas.push(new Array<readonly CelulaCompacta[]>(linhaDaEmpresa.length).fill(SEM_SERVICO));
    }

    linhas.forEach((dias, ordem) => {
      saida.push({ sub, ordem, total: linhas.length, dias });
    });
  });

  return saida;
}

/**
 * ⚠️⚠️ **A MÁQUINA, e é UMA SÓ — usam-na os subcontratados e a linha de espera.**
 *
 * Reparte os serviços de **um grupo** (uma empresa, ou o conjunto das viagens em espera) por
 * linhas ao longo do ano: uma linha nova só nasce quando um serviço começa e todas as que
 * existem estão ocupadas. O resultado tem tantas linhas quantos os serviços do pior dia do ano,
 * que é o mínimo possível.
 *
 * ⚠️ **Extraída em 2026-09-15 para a peça F**, e não copiada: a linha de espera precisa
 * exactamente desta repartição (duas viagens em espera podem cobrir os mesmos dias — sem
 * executor não há índice único que o impeça), e *«duas formulações da mesma regra divergem, e a
 * que divergir é a que ninguém corre»* (regra 8). O comportamento dos subcontratados fica
 * **palavra por palavra** o que era; o que mudou foi o sítio onde vive.
 *
 * ⚠️ **Sem tecto e sem a regra da linha vazia.** Um tecto é o que B28 tinha, e um serviço que
 * não cabe é um serviço que não se vê; a linha vazia é regra da secção e não da repartição.
 */
export function repartirPorLinhas(
  diasDoGrupo: readonly (readonly CelulaCompacta[])[],
): readonly (readonly (readonly CelulaCompacta[])[])[] {
  const nDias = diasDoGrupo.length;
  /** `linhas[r][d]` — o serviço da linha `r` no dia `d`, ou nada. */
  const linhas: (readonly CelulaCompacta[])[][] = [];
  const garantirLinha = (r: number): (readonly CelulaCompacta[])[] => {
    while (linhas.length <= r)
      linhas.push(new Array<readonly CelulaCompacta[]>(nDias).fill(SEM_SERVICO));
    return linhas[r] as (readonly CelulaCompacta[])[];
  };

  /** As linhas **abertas**: `linha` → a identidade do serviço que vem do dia anterior. */
  let abertas = new Map<number, string>();

  for (let d = 0; d < nDias; d++) {
    const servicos = ordenarServicosDoDia(diasDoGrupo[d] ?? []);
    const consumidos = new Set<number>();
    const seguintes = new Map<number, string>();

    // ---- 1. Continuar o que já vinha, NA MESMA LINHA ----------------------
    //
    // ⚠️ É o invariante que dá sentido a tudo o resto: um serviço ocupa **uma** linha do
    //    princípio ao fim. Sem ele a barra partia-se em duas, e duas barras leem-se como
    //    dois serviços.
    for (const [r, chave] of [...abertas.entries()].sort((a, b) => a[0] - b[0])) {
      const i = servicos.findIndex((c, k) => !consumidos.has(k) && identidadeDoServico(c) === chave);
      if (i < 0) continue;
      consumidos.add(i);
      const c = servicos[i];
      if (c !== undefined) garantirLinha(r)[d] = [c];
      seguintes.set(r, chave);
    }

    // ---- 2. Os que começam hoje, na linha livre mais alta -----------------
    for (let k = 0; k < servicos.length; k++) {
      if (consumidos.has(k)) continue;
      const c = servicos[k];
      if (c === undefined) continue;
      let r = 0;
      while (seguintes.has(r)) r++;
      garantirLinha(r)[d] = [c];
      seguintes.set(r, identidadeDoServico(c));
    }

    abertas = seguintes;
  }

  return linhas;
}

// ===========================================================================
// 🔴 A LINHA DE ESPERA — as viagens que saíram do autocarro (peça F §6.3)
// ===========================================================================
//
// 📗 Cliente: *«ou numa linha dummy sem autocarro no final do mapa após os subcontratados, tipo
//    em espera … para o planeador corrigir o mapa manualmente sem destruir as ligações perfeitas
//    entre viagens»*.
//
// ⚠️⚠️ **REUTILIZA A MÁQUINA E NÃO A SECÇÃO, e a distinção é o desenho todo.** A repartição, os
//    offsets acumulados e a aritmética das barras são as mesmas — é o que faz as viagens em
//    espera comportarem-se como qualquer outra linha do mapa sem um único caso especial. O que
//    **não** se reutiliza é o vocabulário: uma viagem em espera **não é um parceiro**. Não tem
//    empresa, não tem base, não tem preço, e ninguém a está a fazer. Dar-lhe `.mapa-sub-*` era
//    **F-20** — duas coisas diferentes com o mesmo nome —, e o leitor que contasse serviços por
//    empresa passava a contar viagens que não estão contratadas a ninguém.

/** Uma linha da secção de espera: uma viagem à espera de autocarro, ao longo do ano. */
export interface LinhaEmEspera {
  /** 0-based. Não há empresa a que pertencer: a ordem é a da própria secção. */
  readonly ordem: number;
  /** Quantas linhas a secção tem. */
  readonly total: number;
  /** O ano inteiro: `dias[d]` tem **zero ou um** serviço. */
  readonly dias: readonly (readonly CelulaCompacta[])[];
}

/**
 * As linhas da secção de espera, **sobre o ano inteiro** — a mesma razão do C₂: a linha é o que
 * a barra e o gesto apontam, e deslocar o mapa não pode mudar o alvo de uma escrita.
 *
 * ⚠️ **Sem viagens em espera devolve a lista VAZIA — e é o caso normal.** Hoje nada na base está
 * em espera. Uma linha vazia permanente afirmava que existe uma secção onde não existe nada, e
 * o ecrã tem de se ler bem com zero: é critério, não acaso.
 */
export function linhasDaEspera(
  grelhaEspera: readonly (readonly CelulaCompacta[])[],
): readonly LinhaEmEspera[] {
  const linhas = repartirPorLinhas(grelhaEspera);
  return linhas.map((dias, ordem) => ({ ordem, total: linhas.length, dias }));
}

/**
 * Que LINHA da secção mostrar quando alguém aponta para «a empresa `sub`, no dia `dia`».
 *
 * ⚠️⚠️ **É aqui que o C₂ muda o «Ver no mapa».** Ele recebe o `id` da empresa — a rota de
 * reserva não conhece linhas nenhumas — e antes disto aterrava na linha da EMPRESA, que era a
 * única. Agora tem de escolher **qual das linhas dela**: a que tem serviço naquele dia.
 *
 * ⚠️ **Sem serviço nesse dia devolve a PRIMEIRA linha da empresa, e não `-1`.** Um destino
 * inexistente fazia o botão não ir a lado nenhum — *«um botão que aterra em lado nenhum é
 * indistinguível de um botão avariado»*, e este projecto já o produziu três vezes. Aterrar na
 * primeira linha da empresa leva o olho à empresa certa, que é a informação que existe.
 *
 * Devolve `-1` só quando a empresa não tem linha nenhuma — o que não acontece, porque toda a
 * empresa tem pelo menos uma.
 */
export function linhaDoServicoSub(
  linhas: readonly LinhaDeSubcontratado[],
  sub: number,
  dia: number,
): number {
  let primeira = -1;
  for (let i = 0; i < linhas.length; i++) {
    const l = linhas[i];
    if (l === undefined || l.sub !== sub) continue;
    if (primeira < 0) primeira = i;
    if ((l.dias[dia] ?? []).length > 0) return i;
  }
  return primeira;
}

/**
 * A forma da secção: a altura de cada linha e os **offsets acumulados**.
 *
 * ⚠️⚠️ **OS OFFSETS SÃO A PARTE QUE NÃO SE PODE ESQUECER.** Três sítios do `Mapa.tsx`
 * calculavam a posição de uma linha por multiplicação (`sc × ALTURA_LINHA_SUB`) — a altura
 * total do contentor, o destino do «Ver no mapa» e o rectângulo de realce. Com uma linha por
 * serviço o índice deixa de ser o da empresa, logo a multiplicação continua a dar um número
 * **plausível** e aponta para a linha errada: *«um realce no sítio errado é pior do que
 * nenhum — manda olhar para o lado errado com confiança»*.
 *
 * 📌 **Todas as linhas medem o mesmo** (é a diferença face a B28, onde a altura dependia das
 * bandas), logo isto é `acumular(n, ALTURA_LINHA_SUB)` sem excepção nenhuma. Continua a ser
 * uma função e não uma multiplicação no sítio de uso, porque **é isso** que impede a terceira
 * aritmética paralela — a mesma razão de `acumular` existir para a frota (D-085).
 */
export interface FormaDaSeccaoSub {
  /** A altura de cada linha, separador incluído. */
  readonly alturas: readonly number[];
  /** `offsets[i]` = píxeis desde o topo da secção até ao topo da linha `i`. Tem `n + 1`. */
  readonly offsets: readonly number[];
  /** A altura da secção sem o título — `offsets[n]`. */
  readonly altura: number;
}

export function formaDaSeccaoSub(
  linhas: readonly LinhaDeSubcontratado[] | readonly unknown[],
): FormaDaSeccaoSub {
  const n = linhas.length;
  const offsets = acumular(n, ALTURA_LINHA_SUB);
  return {
    alturas: new Array<number>(n).fill(ALTURA_LINHA_SUB),
    offsets,
    altura: offsets[n] ?? 0,
  };
}
